import type { $RootStore } from '../../src/stores/RootStore'

export type SessionCapability =
  | 'home'
  | 'calendar'
  | 'workingHours'
  | 'bookings'
  | 'clients'
  | 'team'
  | 'bookingSystem'
  | 'website'
  | 'marketingTools'
  | 'payments'
  | 'stats'
  | 'googleBusinessProfile'
  | 'subscription'
  | 'growthHub'
  | 'accountSettings'
  | 'receptionAccount'
  | 'setupChecklist'
  | 'search'
  | 'benefits'

export type SessionFacts = {
  isReceptionSession: boolean
  salonMemberOnly: boolean
}

export type SessionAccess =
  | { restricted: false }
  | { restricted: true, capabilities: readonly SessionCapability[] }

const RECEPTION_CAPABILITIES: readonly SessionCapability[] = [
  'home',
  'calendar',
  'workingHours',
  'bookings',
  'clients',
  'receptionAccount',
]

const SALON_MEMBER_CAPABILITIES: readonly SessionCapability[] = [
  'home',
  'calendar',
  'bookings',
  'clients',
  'accountSettings',
  'subscription',
  'growthHub',
  'search',
]

export const sessionAccess = (facts: SessionFacts): SessionAccess => {
  if (facts.isReceptionSession) return { restricted: true, capabilities: RECEPTION_CAPABILITIES }
  if (facts.salonMemberOnly) return { restricted: true, capabilities: SALON_MEMBER_CAPABILITIES }

  return { restricted: false }
}

export const hasCapability = (access: SessionAccess, capability: SessionCapability): boolean =>
  !access.restricted || access.capabilities.includes(capability)

export type LandingCapability = Extract<SessionCapability, 'home' | 'calendar'>

export const landingCapability = (access: SessionAccess): LandingCapability =>
  hasCapability(access, 'home') ? 'home' : 'calendar'

export const sessionAccessOf = (rootStore: $RootStore): SessionAccess =>
  sessionAccess({
    isReceptionSession: rootStore.authStore.isReceptionSession,
    salonMemberOnly: rootStore.userStore.salonMemberOnly,
  })

export const allowedBy = (
  access: SessionAccess,
  matches: (capabilities: readonly SessionCapability[]) => boolean,
): boolean => !access.restricted || matches(access.capabilities)

/* Where a session starts, in whatever each platform calls its routes. */
export const landingFor = (
  access: SessionAccess,
  routes: Record<LandingCapability, string>,
): string => routes[landingCapability(access)]
